"use client";

import { useState } from "react";
import Link from "next/link";
import Logo from "./Logo";
import Button from "./Button";
import { AccountView, CharacterEntry } from "@/lib/types";

const SLOT_COUNT = 5;

const STATE_LABEL: Record<CharacterEntry["state"], string> = {
  creating: "依頼中",
  review: "確認待ち",
  active: "稼働中",
  retired: "引退",
};

type Slot = CharacterEntry | null;

function toSlots(characters: CharacterEntry[]): Slot[] {
  const slots: Slot[] = [];
  for (let i = 0; i < SLOT_COUNT; i += 1) {
    slots.push(characters[i] ?? null);
  }
  return slots;
}

/** キャラクター選択画面 */
export default function CharacterSelect({
  account,
  onLogout,
  onNew,
  onRedo,
  onReview,
  onSelect,
  onDeveloperSwitch,
}: {
  account: AccountView;
  onLogout: () => void;
  onNew: () => void;
  onRedo: (entry: CharacterEntry) => void;
  onReview: (entry: CharacterEntry) => void;
  onSelect: (entry: CharacterEntry) => void;
  onDeveloperSwitch: () => void;
}) {
  const [message, setMessage] = useState("");
  const slots = toSlots(account.characters);

  return (
    <div className="panel panel-wide panel-scene panel-select">
      <div className="head head-brand">
        <Logo />
        <div className="head-account">
          <span className="head-user">
            ユーザー名
            <strong>{account.displayName}</strong>
          </span>
          <span className="head-links">
            {account.canOpenAdmin ? (
              <button className="head-link" type="button" onClick={onDeveloperSwitch}>
                開発者用切り替え
              </button>
            ) : null}
            <button className="head-link" type="button" onClick={onLogout}>
              ログアウト
            </button>
          </span>
        </div>
      </div>

      {account.maintenance ? (
        <div className="maintenance">
          <p className="maintenance-title">メンテ中</p>
          <p className="maintenance-text">ただいまメンテナンス中です。終わるまでキャラクターの選択・作成はできません。</p>
        </div>
      ) : (
      <div className="slots">
        {slots.map((slot, index) => (
          <div className={slot ? "slot" : "slot slot-empty"} key={index}>
            <div className="slot-body">
              {slot ? (
                <>
                  <div className={slot.slug ? "slot-name" : "slot-name slot-name-empty"}>{slot.name || slot.slug || "作成を依頼したキャラクター"}</div>
                  <div className="slot-meta">
                    {slot.world} ／ {slot.region} ／{" "}
                    <span className="slot-state">
                      {slot.working ? "進行中" : slot.state === "review" && slot.reworking ? "手直しの依頼中" : STATE_LABEL[slot.state]}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="slot-name slot-name-empty">空き枠</div>
                  <div className="slot-meta">キャラクターを作れます</div>
                </>
              )}
            </div>

            <div className="slot-actions">
              {slot === null ? (
                <Button variant="primary" inline onClick={onNew}>
                  新規
                </Button>
              ) : slot.state === "creating" ? (
                slot.working ? null : (
                  <Button variant="primary" inline onClick={() => onRedo(slot)} disabled={!slot.request}>
                    やり直し
                  </Button>
                )
              ) : slot.state === "review" ? (
                slot.working ? null : (
                  <Button variant="primary" inline onClick={() => onReview(slot)}>
                    {slot.reworking ? "依頼" : "確認"}
                  </Button>
                )
              ) : slot.state === "active" ? (
                <Button variant="primary" inline onClick={() => onSelect(slot)}>
                  選択
                </Button>
              ) : null}

              {slot && slot.state !== "creating" && !slot.working ? (
                <Button variant="danger" inline onClick={() => setMessage("キャラクターの削除は未実装です")}>
                  削除
                </Button>
              ) : null}
            </div>
          </div>
        ))}
      </div>
      )}

      {/* 攻略ガイドの入口。メンテ中も出す */}
      <div className="select-guide">
        <Link className="button" href="/guide">
          攻略ガイド
        </Link>
      </div>

      <p className="message message-info">{message}</p>
    </div>
  );
}
